<script lang="ts">
    import DragVerticalIcon from "../../../svelte/icons/dragVerticalIcon.svelte";
    import type {
        RelatedJobMap,
        View,
        SchedularVehicleAssignment,
    } from '../types';
    import type {ValidationStoreInner} from "../validate";
    import type { PageStateAny } from '../state';
    import {jobIsInSearchStr, itemRelatedToSelect} from "../lib";

    type Props = {
        job: SchedularVehicleAssignment;
        onDragPosStart: (e: MouseEvent, dragPosRect: DOMRect) => void;
        onDragDurLeftStart?:
            | ((e: MouseEvent, leftDragRect: DOMRect) => unknown)
            | null;
        onDragDurRightStart?:
            | ((
            e: MouseEvent,
            startDragDur: number,
            boundingRect: DOMRect,
        ) => void)
            | null;
        left: number;
        top: number;
        widthPx: number;
        heightPx: number;
        isDraggingPos: boolean;
        isDraggingDurRightStart: boolean;
        isDraggingDurLeftStart: boolean;
        view: View;
        pageState: PageStateAny;
        validationStore: ValidationStoreInner;
        canDragLeft: boolean;
        canDragRight: boolean;
        relatedJobsMap: RelatedJobMap;
        isSiteVisit?: boolean;
        onDoubleClick?: () => void;
        /**
         * Pixel distance from the leftmost rendered piece of this job to THIS
         * piece. Set to >0 only for non-first chunks of a weekend-split VA so
         * position-drag treats the click as if it landed on the logical whole
         * bar instead of the clicked chunk.
         */
        posDragLeftOffsetPx?: number;
        /**
         * Full pixel width of the logical VA (covering all chunks + masked
         * gaps). Set on weekend-split VAs so the right-resize handle anchors
         * to the VA's logical left edge instead of the rightmost chunk's left.
         */
        fullJobWidthPx?: number;
    };
    let {
        job = $bindable(),
        onDragPosStart,
        onDragDurLeftStart = null,
        onDragDurRightStart = null,
        left = 0,
        top = 0,
        widthPx = 250,
        heightPx = 50,
        isDraggingPos = false,
        isDraggingDurRightStart = false,
        isDraggingDurLeftStart = false,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        view,
        pageState,
        validationStore,
        canDragLeft,
        canDragRight,
        relatedJobsMap,
        isSiteVisit = false,
        onDoubleClick,
        posDragLeftOffsetPx = 0,
        fullJobWidthPx,
    }: Props = $props();

    const posDragRect = (rect: DOMRect): DOMRect =>
        posDragLeftOffsetPx === 0
            ? rect
            : new DOMRect(rect.left - posDragLeftOffsetPx, rect.top, rect.width, rect.height);

    /**
     * For right-resize math, replace the chunk's width with the full VA's
     * width and re-anchor the rect's left to the VA's logical left edge so
     * `clientX - rect.width` gives the VA's logical left in screen pixels.
     */
    const rightDurRect = (rect: DOMRect): DOMRect => {
        if (fullJobWidthPx === undefined || fullJobWidthPx === rect.width) {
            return rect;
        }
        const adjustedLeft = rect.left + rect.width - fullJobWidthPx;
        return new DOMRect(adjustedLeft, rect.top, fullJobWidthPx, rect.height);
    };

    let lastTapTime = 0;
    let isHighlighted = $derived(
        jobIsInSearchStr(pageState.searchStr, job) &&
        (pageState.selected === null ||
            itemRelatedToSelect(
                pageState.selected?.item,
                job,
                relatedJobsMap,
            ))
    );

    let node: HTMLDivElement;
    let leftDragDiv: HTMLDivElement = $state();

    const draggingNode = document.createElement("div");
    draggingNode.classList.add("drag-job");

    let boundingRect: DOMRect;



    const onMouseDownPos = (e: MouseEvent) => {
        if (e.buttons === 2) {
            return;
        } else if (e.buttons !== 1) {
            return;
        }
        // Note: modified = true is set in applyDropPreview when an actual drag occurs
        document.getSelection()?.empty();
        pageState.searchStr = '';
        boundingRect = node.getBoundingClientRect();
        pageState.selected = {
            type: "job",
            item: job,
            dragging: null,
        }
        onDragPosStart(e, posDragRect(boundingRect));
    };

    const onMouseDownDurRight = (e: MouseEvent) => {
        if (e.buttons !== 1) return;
        if (onDragDurRightStart === null) return;
        pageState.selected = {
            type: "job",
            item: job,
            dragging: null,
        };
        // Note: modified = true is set in applyDropPreview when an actual drag occurs
        const adjusted = rightDurRect(node.getBoundingClientRect());
        onDragDurRightStart(e, e.clientX - adjusted.width, adjusted);
    };

    const onMouseDownDurLeft = (e: MouseEvent) => {
        if (e.buttons !== 1) return;
        if (onDragDurLeftStart === null) return;
        pageState.selected = {
            type: "job",
            item: job,
            dragging: null,
        };
        // Note: modified = true is set in applyDropPreview when an actual drag occurs
        boundingRect = leftDragDiv.getBoundingClientRect();
        onDragDurLeftStart(e, boundingRect);
    };

    const onTouchStartPos = (e: TouchEvent) => {
        const touch = e.touches[0];
        if (!touch) return;
        const now = Date.now();
        if (now - lastTapTime < 300) {
            onDoubleClick?.();
        }
        lastTapTime = now;
        e.preventDefault();
        document.getSelection()?.empty();
        pageState.searchStr = '';
        boundingRect = node.getBoundingClientRect();
        pageState.selected = { type: "job", item: job, dragging: null };
        const fakeEvent = { clientX: touch.clientX, clientY: touch.clientY, preventDefault: () => {} } as unknown as MouseEvent;
        onDragPosStart(fakeEvent, posDragRect(boundingRect));
    };

    const onTouchStartDurRight = (e: TouchEvent) => {
        const touch = e.touches[0];
        if (!touch || onDragDurRightStart === null) return;
        e.preventDefault();
        pageState.selected = { type: "job", item: job, dragging: null };
        const adjusted = rightDurRect(node.getBoundingClientRect());
        const fakeEvent = { clientX: touch.clientX, clientY: touch.clientY, preventDefault: () => {} } as unknown as MouseEvent;
        onDragDurRightStart(fakeEvent, touch.clientX - adjusted.width, adjusted);
    };

    const onTouchStartDurLeft = (e: TouchEvent) => {
        const touch = e.touches[0];
        if (!touch || onDragDurLeftStart === null) return;
        e.preventDefault();
        pageState.selected = { type: "job", item: job, dragging: null };
        boundingRect = leftDragDiv.getBoundingClientRect();
        const fakeEvent = { clientX: touch.clientX, clientY: touch.clientY, preventDefault: () => {} } as unknown as MouseEvent;
        onDragDurLeftStart(fakeEvent, boundingRect);
    };

    let selected = $derived(pageState.selected?.item?.id === job.id);
    // TODO: look into why the types are so wrong here
    // let duration = $derived(
    //     pageState.selected?.dragging?.dropPos &&
    //     pageState.selected?.item.uuid === job.uuid
    //         ? pageState.selected?.dragging.dropPos.durationSec
    //         : job.duration,
    // );

    let displayDurDrag = $derived(widthPx > 100);
    let dragLeftWidth = $state(0);
    let dragRightWidth = $state(0);

    let durDragWidth = $derived(
        displayDurDrag ? dragLeftWidth + dragRightWidth : 0,
    );

    const hasErrorsFn = (val: ValidationStoreInner): string | null => {
        const isMissingJobType = val.missingJobType.some(j => j.id === job.id);
        const isMissingPurpose = val.missingPurpose.some(j => j.id === job.id);

        if (isMissingJobType && isMissingPurpose) return 'Missing Job Type and Purpose';
        if (isMissingJobType) return 'Missing Job Type';
        if (isMissingPurpose) return 'Missing Purpose';
        return null;
    };
    let errorMessage = $derived(hasErrorsFn(validationStore));

    let primaryCustomerName = $derived.by(() => {
        const customers = job.job?.customers ?? [];
        if (customers.length === 0) return null;
        const primary = customers.find((c: any) => c.pivot?.is_primary) ?? customers[0];
        return primary?.name ?? null;
    });
</script>

<div
    class="drag-job"
    bind:this={node}
    class:dragging-pos={isDraggingPos}
    class:dragging-dur={isDraggingDurLeftStart || isDraggingDurRightStart}
    class:site-visit={isSiteVisit}
    style:--width={`${widthPx}px`}
    style:--height={`${heightPx}px`}
    style:--left={`${left}px`}
    style:--top={`${top}px`}
    style:--sat={`${job.is_task ? 0 : (isHighlighted ? 80 : 15)}%`}
    style:--hue={`${job.is_task ? 0 : job.hue}`}
    style:--light={job.is_task ? '100%' : 'var(--scheduler-job-light)'}
    style:--job-color={job.start === null ? 'var(--event-gray-scheduler)' : (job.color ? `var(--event-${job.color}-scheduler)` : undefined)}
    class:selected
    class:dull={!isHighlighted && !job.is_task}
    class:has-error={errorMessage}
    data-job-id={job.id}
>
    {#if canDragLeft && displayDurDrag && !isSiteVisit}
        <div
            role="none"
            class="drag-duration-handler"
            onmousedown={onMouseDownDurLeft}
            ontouchstart={onTouchStartDurLeft}
            ondragstart={() => false}
            bind:this={leftDragDiv}
            bind:clientWidth={dragLeftWidth}
        >
            <DragVerticalIcon/>
        </div>
    {/if}
    <div
        role="none"
        class="drag-pos"
        onmousedown={onMouseDownPos}
        ondblclick={() => onDoubleClick?.()}
        ontouchstart={onTouchStartPos}
        oncontextmenu={(e) => {
            e.preventDefault();
            return false;
        }}
        ondragstart={() => false}
        style:--width={`${widthPx - durDragWidth}px`}
    >
        {#if isSiteVisit}
            <div class="site-visit-content">
                <span class="site-visit-postcode">{job.job?.address?.postcode ?? ''}</span>
            </div>
        {:else}
            <div class="content overflow-hidden" style:--left={`${job.start ? 250 : 0}px`}>
                <div class="top">
                    <p class="text-xs font-semibold leading-tight truncate">{job.site_name ?? "Missing Name"}</p>
                    {#if (job as any).job_phase}
                        <span class="phase-badge" title={(job as any).job_phase.label}>
                            P{(job as any).job_phase.sequence}
                        </span>
                        {#if (job as any).job_phase.hire_end_date && (job as any).job_type === 'full strike' && new Date((job as any).job_phase.hire_end_date) < new Date((job as any).job_phase.strike_end_date)}
                            <span class="left-standing-badge" title="Left standing — hire ended, awaiting takedown">LS</span>
                        {/if}
                    {/if}
                </div>
                <div class="flex items-center gap-1 overflow-hidden">
                    <p class="text-xs leading-tight truncate">{primaryCustomerName ?? "-"}</p>
                    {#if job.invoiced}
                        <span class="invoiced-badge" title={job.invoiced_date ? `Invoiced ${new Date(job.invoiced_date).toLocaleDateString('en-GB')}` : 'Invoiced'}>INV</span>
                    {/if}
                </div>

                <div class="employees-row">
                    {#if job.employeeAssignments && job.employeeAssignments.length > 0}
                        {@const activeAssignments = Array.from(job.employeeAssignments.values()).filter(a => a.employee && !a.deleted)}
                        {#each activeAssignments as assignedEmployee (assignedEmployee.id)}
                            <span class="employee-name-tag">{assignedEmployee.employee.first_name.charAt(0).toUpperCase()}. {assignedEmployee.employee.last_name}</span>
                        {/each}
                    {:else}
                        <span class="text-xs leading-tight opacity-60">No employees</span>
                    {/if}
                </div>
            </div>
        {/if}
    </div>
    {#if canDragRight && displayDurDrag && !isSiteVisit}
        <div
            role="none"
            class="drag-duration-handler"
            onmousedown={onMouseDownDurRight}
            ontouchstart={onTouchStartDurRight}
            bind:clientWidth={dragRightWidth}
            ondragstart={() => false}
        >
            <DragVerticalIcon/>
        </div>
    {/if}
</div>

<style lang="scss">
    .drag-job {
        opacity: 1;
        border-radius: 5px;
        /* Use CSS variable color if available, otherwise fall back to HSL */
        background: var(--job-color, hsl(var(--hue) var(--sat) var(--light)));
        color: hsl(var(--clr-on-surface-1));
        width: var(--width);
        height: var(--height);
        display: grid;
        grid-template-columns: max-content 1fr max-content;
        justify-content: space-between;
        align-items: center;
        z-index: 10;
        left: var(--left);
        top: var(--top);
        outline: solid 2px transparent;
        transition: outline 200ms, box-shadow 200ms, opacity 200ms, background 200ms;
        position: relative;
        border: 1px solid #00000070;

        &.selected {
            outline: solid 5px var(--clr-primary);
        }

        &.has-error {
            outline: 5px solid var(--error);
            outline-offset: -1px;
            animation: pulse-error 2s ease-in-out infinite;
        }

        &.dull {
            filter: saturate(0.2) brightness(1.1);
        }
    }

    @keyframes pulse-error {
        0%, 100% { outline-color: var(--error); }
        50% { outline-color: hsl(0 84% 60% / 0.4); }
    }

    .dragging-pos,
    .dragging-dur {
        z-index: 100;
        box-shadow: 0 0 2rem 0 #00000070;
        position: fixed;
        opacity: 0.85;
    }

    .drag-pos {
        cursor: grab;
        width: var(--width);
        height: var(--height);
        align-items: center;
        display: flex;
        position: relative;

        .content {
            position: sticky;
            left: var(--left);
            right: 0px;
            display: flex;
            flex-direction: column;
            gap: 0.125rem;
            width: 100%;
            min-width: 0;
            height: 95%;
            padding: 0.25rem 0.375rem;
            overflow: hidden;

            .top,
            .bottom,
            .employees-row {
                min-width: 0;
                overflow: hidden;
            }

            .top,
            .bottom {
                display: flex;
                gap: 0.5rem;
                align-items: center;
                .job-title {
                    font-size: 0.8rem;
                }
            }

            .invoiced-badge {
                flex-shrink: 0;
                font-size: 0.65rem;
                font-weight: 600;
                line-height: 1;
                padding: 0.15rem 0.4rem;
                border-radius: 999px;
                background: var(--success);
                color: var(--success-foreground);
                text-transform: uppercase;
                letter-spacing: 0.03em;
            }

            .phase-badge {
                flex-shrink: 0;
                font-size: 0.65rem;
                font-weight: 600;
                line-height: 1;
                padding: 0.15rem 0.4rem;
                border-radius: 999px;
                background: hsl(0 0% 0% / 0.35);
                color: inherit;
                text-transform: uppercase;
                letter-spacing: 0.03em;
                border: 1px solid hsl(0 0% 100% / 0.25);
            }

            .left-standing-badge {
                flex-shrink: 0;
                font-size: 0.65rem;
                font-weight: 600;
                line-height: 1;
                padding: 0.15rem 0.4rem;
                border-radius: 999px;
                background: hsl(var(--warning) / 0.8);
                color: hsl(0 0% 0%);
                text-transform: uppercase;
                letter-spacing: 0.03em;
            }

            .top :global(svg) {
                min-width: 1.5rem;
            }

            .employees-row {
                display: flex;
                flex-wrap: wrap;
                gap: 0.125rem 0.375rem;
                min-width: 0;
                overflow: hidden;
            }

            .employee-name-tag {
                font-size: 0.7rem;
                line-height: 1.25;
                color: inherit;
                opacity: 0.85;
                white-space: nowrap;
            }
        }
    }

    .drag-duration-handler {
        cursor: col-resize;
        height: 100%;
        display: flex;
        align-items: center;
        background: #00000020;
    }

    .job-vehicles {
        font-size: 0.8rem;
        color: hsl(var(--clr-on-surface-2));
    }

    .site-visit-content {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        overflow: hidden;
    }

    .site-visit-postcode {
        writing-mode: vertical-rl;
        text-orientation: mixed;
        font-size: 0.7rem;
        font-weight: 600;
        letter-spacing: 0.05em;
        color: inherit;
    }
</style>
